import { ApolloLink, FetchResult, Observable, Operation } from '@apollo/client';
import { demoReady } from 'demo/bootState';

type LoadLink = () => Promise<ApolloLink>;

const loadBootedDemoLink: LoadLink = async () => {
  // Queries fired while the app is still evaluating must not reach the store before it exists.
  await demoReady;
  const { createBootedDemoLink } = await import('demo/graphql/link');
  return createBootedDemoLink();
};

/**
 * The only demo module graphql/client.js imports statically. The schema, fixtures and
 * resolvers live behind import(), so production bundles never download them.
 */
export const createDeferredDemoLink = (loadLink: LoadLink = loadBootedDemoLink): ApolloLink => {
  let linkPromise: Promise<ApolloLink> | undefined;

  return new ApolloLink(
    (operation: Operation) =>
      new Observable<FetchResult>((observer) => {
        let subscription: ReturnType<Observable<FetchResult>['subscribe']> | undefined;
        let closed = false;

        const run = async () => {
          try {
            linkPromise ??= loadLink();
            const link = await linkPromise;
            if (closed) {
              return;
            }
            const result = link.request(operation);
            if (!result) {
              observer.complete();
              return;
            }
            subscription = result.subscribe(observer);
          } catch (error) {
            // Let the next operation try loading again.
            linkPromise = undefined;
            observer.error(error);
          }
        };

        run();

        return () => {
          closed = true;
          subscription?.unsubscribe();
        };
      })
  );
};
